import { useEffect, useMemo, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import type { Message } from '../types/events';
import {
  LOCATION_SCOPE_TOOL_CALL_ID,
  PRECHAT_FORM_TOOL_CALL_ID,
} from '../hooks/useChatEventHandlers';
import type { ChatSettings } from '../hooks/useRouterSettings';
import type { WhatsAppHandoff } from '../types/events';
import type { ConnectionStatus } from '../hooks/useWebSocket';
import { ChatHeader } from './ChatHeader';
import { MessageList } from './MessageList';
import { WhatsAppHandoffBanner } from './WhatsAppHandoffBanner';
import { ChatInput } from './ChatInput';
import { BottomSheet } from './BottomSheet';
import { ToolUiPanel, getToolUiTitle } from './ToolUiPanel';

// --- Durable bottom-sheet state ----------------------------------------------
// A dismissed (or answered) tool prompt must NOT re-open when ChatWindow
// remounts (chat closed/reopened), on a page reload, or after a websocket
// reconnect — in all of which the pending tool-UI message survives in
// useMessages (which lives in Widget) while ChatWindow's component state would
// otherwise reset and re-trigger the auto-present effect.
//
// We track dismissal/answer per tool_call_id (not as a single global flag) and
// key storage by the stable widget id — NOT the session id. The backend
// rotates the session id whenever it can't restore the previous session (a
// reconnect after an idle drop, a server restart, eviction), so a session-keyed
// flag silently resets and the dismissed sheet pops back up. tool_call_ids are
// unique, so a brand-new conversation's prompts (new ids) are never matched by
// an old dismissal — keying by widget id is durable without leaking across
// conversations.
interface PersistedSheetState {
  /** Confirmation summaries for answered tool UIs, keyed by tool_call_id. */
  answered: Record<string, string>;
  /** tool_call_ids the user dismissed without answering. */
  dismissed: string[];
}

const SHEET_STATE_PREFIX = 'cp-widget-sheet-';
/** Cap the dismissed list so a long-lived tab can't grow storage unbounded. */
const MAX_DISMISSED = 200;

function loadSheetState(persistKey?: string | null): PersistedSheetState {
  const empty: PersistedSheetState = { answered: {}, dismissed: [] };
  if (!persistKey || typeof sessionStorage === 'undefined') return empty;
  try {
    const raw = sessionStorage.getItem(SHEET_STATE_PREFIX + persistKey);
    if (!raw) return empty;
    const parsed = JSON.parse(raw) as Partial<PersistedSheetState>;
    return {
      answered: parsed.answered && typeof parsed.answered === 'object' ? parsed.answered : {},
      dismissed: Array.isArray(parsed.dismissed) ? parsed.dismissed.filter((id) => typeof id === 'string') : [],
    };
  } catch {
    return empty;
  }
}

function saveSheetState(persistKey: string | null | undefined, state: PersistedSheetState): void {
  if (!persistKey || typeof sessionStorage === 'undefined') return;
  try {
    const trimmed: PersistedSheetState = {
      answered: state.answered,
      dismissed: state.dismissed.slice(-MAX_DISMISSED),
    };
    sessionStorage.setItem(SHEET_STATE_PREFIX + persistKey, JSON.stringify(trimmed));
  } catch {
    // Storage unavailable (private mode / quota) — fall back to in-memory only.
  }
}

export interface ChatWindowProps {
  /** Close handler */
  onClose: () => void;
  /** Chat settings from API (optional) */
  settings: ChatSettings | null;
  /** Array of messages to display */
  messages: Message[];
  /** Whether a message is currently streaming */
  isStreaming: boolean;
  /** Whether assistant typing indicator is active */
  isAssistantTyping?: boolean;
  /** WebSocket connection status */
  connectionStatus: ConnectionStatus;
  /** Handler for sending a message */
  onSendMessage: (message: string) => void;
  /** Handler for sending tool response payloads */
  onSendToolResponse: (toolCallId: string, toolName: string, result: Record<string, unknown>) => void;
  /** Last error message (optional) */
  error?: string | null;
  /** Clear error handler */
  onClearError?: () => void;
  /**
   * Whether the server is still waiting for the pre-chat contact details (CAIV-362).
   *
   * Comes from the settings, not from a card in the transcript: the server refuses the message
   * either way, so a visitor whose card went missing would otherwise get an open message box, a
   * refusal, and nothing to act on.
   */
  prechatRequired?: boolean;
  /** Horizontal viewport padding to preserve around chat window */
  horizontalViewportPadding?: number;
  /** Render edge-to-edge  */
  isFullscreen?: boolean;
  /**
   * Whether the on-screen keyboard is currently open (fullscreen mobile only).
   * When it is, the bottom home-indicator safe-area padding is dropped: the
   * keyboard already covers that inset, so keeping it leaves a visible gap
   * between the input and the keyboard.
   */
  keyboardOpen?: boolean;
  /**
   * Stable per-session key (the conversation/session id) used to persist the
   * bottom sheet's answered + dismissed state so it survives this component
   * being unmounted (chat closed) and remounted, and a page reload. Omit to
   * keep the state in-memory only (e.g. in unit tests).
   */
  persistKey?: string | null;
  /**
   * Flips the layout direction, when the club lets visitors do that
   * (`layout.allow_direction_change`). Undefined hides the control entirely — most widgets never
   * show it, so it must leave no trace in the header.
   */
  onToggleDirection?: () => void;
  /**
   * The "continue on WhatsApp" banner, or null when there is none or the visitor closed
   * it (CAIV-1063). Whether one exists is decided server-side.
   */
  whatsAppHandoff?: WhatsAppHandoff | null;
  /** Closes the banner for this browser session. */
  onDismissWhatsAppHandoff?: () => void;
}

/**
 * Main chat window component that orchestrates the header, message list, and input.
 *
 * Features:
 * - 384w x 512h px container with rounded-2xl and shadow-2xl styling
 * - Header with title, connection status, and close button
 * - Scrollable message list with auto-scroll to bottom
 * - Input area with send button
 * - Error display toast
 */
export function ChatWindow({
  onClose,
  settings,
  messages,
  isStreaming,
  isAssistantTyping = false,
  connectionStatus,
  onSendMessage,
  onSendToolResponse,
  error,
  onClearError,
  prechatRequired,
  horizontalViewportPadding = 16,
  isFullscreen = false,
  keyboardOpen = false,
  persistKey,
  onToggleDirection,
  whatsAppHandoff,
  onDismissWhatsAppHandoff,
}: ChatWindowProps) {
  const applyBottomSafeArea = isFullscreen && !keyboardOpen;
  const { t } = useTranslation('widget');
  // Extract customization from settings (matching backend API response)
  const title = settings?.label ?? t('defaultTitle');
  const headerColor = settings?.settings.primaryColor ?? '#3b82f6';
  const userMessageBgColor = settings?.settings.accentColor ?? '#3b82f6';
  const userMessageTextColor = settings?.settings.userTextColor ?? '#ffffff';
  const botMessageBgColor = settings?.settings.secondaryColor ?? '#f3f4f6';
  const backgroundColor = settings?.settings.backgroundColor ?? '#ffffff';
  const textColor = settings?.settings.textColor ?? '#1f2937';
  const profilePictureUrl = settings?.settings.profilePictureUrl ?? '';
  const chatbotLogoUrl = settings?.settings.chatbotLogoUrl ?? '';
  const logoStyle = settings?.settings.logoStyle ?? 'square_rounded';
  const logoFit = settings?.settings.logoFit ?? 'cover';
  const placeholderText = t('input.placeholder');

  const isConnected = connectionStatus === 'connected';
  const isUnavailable = connectionStatus === 'unavailable';

  const responsiveWidth = isFullscreen
    ? '100%'
    : `min(384px, calc(100vw - ${Math.max(0, horizontalViewportPadding * 2)}px))`;

  // --- Interactive tool UIs presented as a bottom sheet ---------------------
  // Confirmation summaries for answered tool UIs, keyed by tool_call_id. Seeded
  // from persisted state so answered prompts stay answered across remount/reload.
  const [answeredSummaries, setAnsweredSummaries] = useState<Record<string, string>>(
    () => loadSheetState(persistKey).answered
  );
  // A prompt can also arrive already answered: the restored location bubble carries its own
  // `📍 <club>` confirmation, because the club lives on the conversation rather than in this tab's
  // storage, and a reload in a fresh tab has no sheet state to read it from. This tab's own state
  // wins, so a club changed a moment ago is not overwritten by the bubble that rebuilt it.
  const messageSummaries = useMemo(() => {
    const fromMessages: Record<string, string> = {};
    for (const message of messages) {
      const toolUi = message.metadata?.tool_ui;
      const summary = message.metadata?.tool_ui_summary;
      if (toolUi && typeof summary === 'string' && summary) {
        fromMessages[toolUi.tool_call_id] = summary;
      }
    }
    return fromMessages;
  }, [messages]);
  // The message wins where it has something to say: for the location card the server echoes the
  // club it actually stored, so a refused change must not leave this tab's optimistic label up.
  // Everything else has no message-carried summary, so its sheet state still applies.
  //
  // The two cards the widget mints itself are the exception in both directions (CAIV-362): the
  // server rebuilds them on every settings event, so their *absence* of a summary is an answer
  // too. Letting this tab's stored one stand in would paint a club as chosen that the server
  // never received, leaving the visitor with no question to answer, no form, and a message box
  // that refuses them.
  const effectiveSummaries = useMemo(() => {
    const merged: Record<string, string> = { ...answeredSummaries, ...messageSummaries };
    for (const id of [LOCATION_SCOPE_TOOL_CALL_ID, PRECHAT_FORM_TOOL_CALL_ID]) {
      if (messageSummaries[id] === undefined) delete merged[id];
    }
    return merged;
  }, [messageSummaries, answeredSummaries]);
  // tool_call_ids the user dismissed without answering. Held in a ref (read by
  // the auto-present effect, doesn't need to trigger a render) and seeded from
  // persisted state so a dismissal survives a remount, reload, or reconnect.
  const dismissedRef = useRef<Set<string>>(new Set(loadSheetState(persistKey).dismissed));
  // Which tool UI message the sheet is currently bound to (kept while dismissed
  // so a half-filled form survives reopening), and whether it is visible.
  const [sheetToolCallId, setSheetToolCallId] = useState<string | null>(null);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const autoPresentedRef = useRef<string | null>(null);

  // The most recent tool UI message that has not yet been answered.
  const pendingToolMessage = useMemo(() => {
    for (let i = messages.length - 1; i >= 0; i--) {
      const toolUi = messages[i].metadata?.tool_ui;
      if (toolUi && effectiveSummaries[toolUi.tool_call_id] === undefined) {
        return messages[i];
      }
    }
    return null;
  }, [messages, effectiveSummaries]);
  const pendingToolCallId = pendingToolMessage?.metadata?.tool_ui?.tool_call_id ?? null;

  // Auto-present the sheet once when a pending tool UI arrives, unless the user
  // already dismissed that prompt. Dismissal is tracked per tool_call_id and
  // persisted, so a re-emit/reconnect/reload that brings the same prompt back
  // (the backend restores it under its original tool_call_id) cannot re-pop a
  // sheet the user closed — while a genuinely new prompt (new id) still opens.
  // The inline card always remains for manual reopening.
  useEffect(() => {
    if (!pendingToolCallId) return;
    // The pre-chat form is not auto-presented (CAIV-362). It gates the whole chat rather than
    // answering something the visitor asked for, and opening a full-height sheet over a chat
    // nobody has used yet reads as a wall rather than an invitation. The card sits in the
    // transcript and they open it.
    if (pendingToolCallId === PRECHAT_FORM_TOOL_CALL_ID) return;
    if (dismissedRef.current.has(pendingToolCallId)) return;
    if (autoPresentedRef.current === pendingToolCallId) return;
    autoPresentedRef.current = pendingToolCallId;
    setSheetToolCallId(pendingToolCallId);
    setIsSheetOpen(true);
  }, [pendingToolCallId]);

  // Which answered prompt, if any, may be opened again. Only the location question qualifies,
  // and only while the club still offers the shortlist: re-answering a date or a contact form
  // after the fact would rewrite a booking that has already been made, whereas the club a
  // conversation is about is a standing fact the visitor is allowed to correct.
  const reopenableToolCallId = useMemo(() => {
    const card = messages.find(
      (m) => m.metadata?.tool_ui?.tool_call_id === LOCATION_SCOPE_TOOL_CALL_ID
    );
    // `tool_ui_locked` is the server's word that the club is settled: a prospect exists at it, or
    // the club switched changing off. The confirmation stays, as a statement rather than a door.
    if (!card || card.metadata?.tool_ui_locked === true) return null;
    const options = card.metadata?.tool_ui?.ui_config?.all_locations;
    const hasOptions = Array.isArray(options) && options.length > 1;
    return hasOptions ? LOCATION_SCOPE_TOOL_CALL_ID : null;
  }, [messages]);

  // The message backing the sheet — rendered while the prompt is unanswered, or when it is one
  // the visitor is allowed to answer again.
  const sheetMessage = useMemo(() => {
    if (!sheetToolCallId) return null;
    const answered = effectiveSummaries[sheetToolCallId] !== undefined;
    if (answered && sheetToolCallId !== reopenableToolCallId) return null;
    return messages.find((m) => m.metadata?.tool_ui?.tool_call_id === sheetToolCallId) ?? null;
  }, [messages, sheetToolCallId, effectiveSummaries, reopenableToolCallId]);
  const sheetToolUi = sheetMessage?.metadata?.tool_ui;
  const isReopenedAnswer =
    !!sheetToolUi && effectiveSummaries[sheetToolUi.tool_call_id] !== undefined;

  const handleOpenToolSheet = (toolCallId: string) => {
    setSheetToolCallId(toolCallId);
    setIsSheetOpen(true);
  };

  const handleDismissSheet = () => {
    setIsSheetOpen(false);
    if (sheetToolCallId) {
      dismissedRef.current.add(sheetToolCallId);
      saveSheetState(persistKey, {
        answered: answeredSummaries,
        dismissed: [...dismissedRef.current],
      });
    }
  };

  const handleToolPanelSubmit = (result: Record<string, unknown>, summary?: string) => {
    if (!sheetToolUi) return;
    const nextAnswered = {
      ...answeredSummaries,
      [sheetToolUi.tool_call_id]: summary ?? t('sheet.done'),
    };
    setAnsweredSummaries(nextAnswered);
    // Answering supersedes a prior dismissal of the same prompt.
    dismissedRef.current.delete(sheetToolUi.tool_call_id);
    setIsSheetOpen(false);
    // Unbind, so reopening this prompt mounts a fresh panel. ToolUiPanel keeps a submitted-once
    // ref and is keyed by tool_call_id, so leaving the answered location card bound kept that ref
    // set and silently swallowed every pick after the first.
    setSheetToolCallId(null);
    saveSheetState(persistKey, {
      answered: nextAnswered,
      dismissed: [...dismissedRef.current],
    });
    onSendToolResponse(sheetToolUi.tool_call_id, sheetToolUi.tool_name, result);
  };

  return (
    <div
      className="bg-white flex flex-col overflow-hidden"
      style={{
        width: responsiveWidth,
        maxWidth: responsiveWidth,
        height: isFullscreen ? '100%' : '512px',
        backgroundColor,
        color: textColor,
        borderRadius: isFullscreen ? 0 : '16px',
        boxShadow: isFullscreen ? 'none' : '0 25px 50px -12px rgba(0, 0, 0, 0.25)',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Header */}
      <ChatHeader
        title={title}
        connectionStatus={connectionStatus}
        headerColor={headerColor}
        logoUrl={chatbotLogoUrl}
        logoStyle={logoStyle}
        logoFit={logoFit}
        onClose={onClose}
        onToggleDirection={onToggleDirection}
        safeAreaTop={isFullscreen}
      />

      {/* Error toast */}
      {error && (
        <div
          className="bg-red-50 border-b border-red-200 px-4 py-2 flex items-center justify-between shrink-0"
          style={{
            backgroundColor: '#fef2f2',
            borderBottom: '1px solid #fecaca',
            padding: '8px 16px',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexShrink: 0,
          }}
        >
          <span className="text-red-700 text-sm" style={{ color: '#b91c1c', fontSize: '14px' }}>{error}</span>
          {onClearError && (
            <button
              onClick={onClearError}
              className="text-red-500 hover:text-red-700 text-sm font-medium"
              style={{ color: '#ef4444', fontSize: '14px', fontWeight: 500, background: 'none', border: 'none', cursor: 'pointer' }}
              type="button"
            >
              {t('buttons.close', { ns: 'common' })}
            </button>
          )}
        </div>
      )}

      {whatsAppHandoff && onDismissWhatsAppHandoff && (
        <WhatsAppHandoffBanner
          handoff={whatsAppHandoff}
          onDismiss={onDismissWhatsAppHandoff}
        />
      )}

      {/* Body: message list + input. Relative + clipped so the bottom sheet can
          cover this region while the header above stays visible. */}
      <div
        style={{
          position: 'relative',
          flex: 1,
          display: 'flex',
          flexDirection: 'column',
          minHeight: 0,
          overflow: 'hidden',
        }}
      >
        {/* Message list */}
        <MessageList
          messages={messages}
          userMessageBgColor={userMessageBgColor}
          userMessageTextColor={userMessageTextColor}
          botMessageBgColor={botMessageBgColor}
          backgroundColor={backgroundColor}
          textColor={textColor}
          answeredSummaries={effectiveSummaries}
          reopenableToolCallId={reopenableToolCallId}
          onOpenToolSheet={handleOpenToolSheet}
          avatarUrl={profilePictureUrl}
          // "Assistant is typing" is a promise of a reply. Once the connection
          // is terminally unavailable nothing is coming, and a starter click
          // switches the indicator on before the send either way (Widget.tsx),
          // so suppress it here rather than trusting every caller (CAIV-1041).
          isAssistantTyping={isAssistantTyping && !isUnavailable}
          trackKeyboard={isFullscreen}
        />

        {/* Input area */}
        {isUnavailable ? (
          <div
            className="widget-unavailable"
            role="status"
            style={{
              padding: '16px',
              paddingBottom: applyBottomSafeArea ? 'calc(16px + env(safe-area-inset-bottom))' : '16px',
              textAlign: 'center',
              flexShrink: 0,
            }}
          >
            <p className="widget-unavailable__title" style={{ fontWeight: 600, marginBottom: '4px' }}>{t('unavailable.title')}</p>
            <p className="widget-unavailable__body" style={{ fontSize: '14px', color: '#64748b' }}>{t('unavailable.body')}</p>
          </div>
        ) : (
          <ChatInput
            onSend={onSendMessage}
            disabled={!isConnected || prechatRequired === true}
            isStreaming={isStreaming}
            placeholder={placeholderText}
            primaryColor={headerColor}
            containerBackgroundColor={backgroundColor}
            safeAreaBottom={applyBottomSafeArea}
            autoFocus={!isFullscreen}
          />
        )}

        {/* Interactive tool UI bottom sheet */}
        {sheetToolUi && (
          <BottomSheet
            open={isSheetOpen}
            onClose={handleDismissSheet}
            title={getToolUiTitle(t, sheetToolUi.ui_type)}
            // Only the question asked before the conversation starts is
            // mandatory: it scopes everything that follows, and a visitor who
            // waves it away lands in the unattributed-conversation case this
            // whole feature exists to remove. Every other sheet, including a
            // location_picker raised mid-conversation by a booking tool, stays
            // dismissible.
            // ... and once it has been answered the visitor already gave us a club, so
            // reopening it to look at the options must not trap them again.
            dismissible={
              sheetToolUi.tool_call_id !== LOCATION_SCOPE_TOOL_CALL_ID || isReopenedAnswer
            }
          >
            <ToolUiPanel
              key={sheetToolUi.tool_call_id}
              toolUi={sheetToolUi}
              onSubmit={handleToolPanelSubmit}
            />
          </BottomSheet>
        )}
      </div>
    </div>
  );
}

export default ChatWindow;
